/**
 * Cookie consent, in the shape GTM and GA4 expect (Consent Mode v2).
 *
 * The mechanism matters: consent is not "don't load GTM". GTM loads either way
 * and reads these signals, so tags that respect consent degrade to cookieless
 * pings rather than vanishing, and a later grant is applied without a reload.
 * That is also why the default has to be pushed BEFORE the container — a tag
 * that fires before any signal arrives is treated as granted.
 */

export const CONSENT_STORAGE_KEY = "hoku-cookie-consent";

export type ConsentChoice = "granted" | "denied";

/**
 * The four Consent Mode v2 signals we set. `analytics_storage` covers GA4;
 * `ad_*` cover the Meta Pixel and any future remarketing tag.
 *
 * `functionality_storage` and `security_storage` are deliberately absent —
 * they are strictly necessary, always granted, and not ours to withhold.
 */
const SIGNALS = [
  "analytics_storage",
  "ad_storage",
  "ad_user_data",
  "ad_personalization",
] as const;

type ConsentState = Record<(typeof SIGNALS)[number], ConsentChoice>;

const stateFor = (choice: ConsentChoice): ConsentState =>
  Object.fromEntries(SIGNALS.map((signal) => [signal, choice])) as ConsentState;

/**
 * The canonical gtag shim, deliberately not written with rest parameters.
 *
 * A consent command has to reach GTM as a genuine `arguments` object, which it
 * reads positionally. Pushing a plain array instead looks identical in the
 * dataLayer and silently does nothing — it is the single detail that most often
 * breaks Consent Mode.
 */
function gtag(..._args: unknown[]): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

/**
 * The inline script that must run BEFORE the GTM container.
 *
 * Emitted as a string rather than called, because it has to be a
 * `beforeInteractive` script tag in the document head — by the time React
 * hydrates, tags may already have fired, and a tag that fires before any signal
 * arrives is treated as granted. Defaults to denied and re-grants immediately
 * for a returning visitor who already said yes, so their choice survives a
 * reload without a flash of denied state.
 */
export const CONSENT_DEFAULT_SCRIPT = `
(function(){
  window.dataLayer = window.dataLayer || [];
  function gtag(){ window.dataLayer.push(arguments); }
  gtag('consent', 'default', {
    'analytics_storage': 'denied',
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
    'wait_for_update': 500
  });
  try {
    if (localStorage.getItem('${CONSENT_STORAGE_KEY}') === 'granted') {
      gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted'
      });
    }
  } catch (e) {}
})();
`.trim();

/** Reads a previously stored choice. Returns null when nothing was decided. */
export function readStoredConsent(): ConsentChoice | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return stored === "granted" || stored === "denied" ? stored : null;
  } catch {
    // Private mode, or site data blocked. Treated as "not yet decided", which
    // means denied-by-default and the banner shows again.
    return null;
  }
}

/**
 * Fired on the window when consent changes in THIS tab. The native `storage`
 * event only reaches other tabs, so without this the components reading the
 * choice would not re-render for the person who just clicked.
 */
const CONSENT_EVENT = "hoku:consent-change";

/** Records the choice and tells GTM about it immediately. */
export function setConsent(choice: ConsentChoice): void {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, choice);
  } catch {
    // Storage unavailable — the signal below still applies for this pageview.
  }
  gtag("consent", "update", stateFor(choice));
  window.dispatchEvent(new Event(CONSENT_EVENT));
}

/**
 * `useSyncExternalStore` plumbing for the stored choice.
 *
 * Used instead of reading localStorage in an effect: the server has no access
 * to it, and `getServerSnapshot` returning "granted" means the server renders
 * as though the question were already answered. The banner is therefore absent
 * from the server HTML and appears only for visitors who genuinely have not
 * decided — no hydration mismatch, and no flash of a banner for the majority
 * who already have.
 */
export function subscribeToConsent(onChange: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === CONSENT_STORAGE_KEY) onChange();
  };
  window.addEventListener("storage", onStorage);
  window.addEventListener(CONSENT_EVENT, onChange);
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(CONSENT_EVENT, onChange);
  };
}

export const getConsentSnapshot = (): ConsentChoice | null => readStoredConsent();

/** See subscribeToConsent — deliberately not null. */
export const getConsentServerSnapshot = (): ConsentChoice | null => "granted";
